import useTranslation from '@hooks/useTranslation';
import { styled } from '@mui/material';
import MUIBox from '@mui/material/Box';
import MUIDivider from '@mui/material/Divider';
import MUITypography from '@mui/material/Typography';
import OrderPartsList from '@pages/OrdersPages/components/OrderDetails/OrderPartsList';
import type { Order } from '@partful/shared-types';
import { formatDateWithTime } from '@services/formatDate';
import {
  OrderDetailsData,
  OrderDetailsDataCell,
  OrderDetailsDataHeader,
  OrderDetailsDataRow,
  OrderDetailsHeader,
  OrderDetailsMainContent,
  OrderDetailsPanel,
  OrderDetailsPanelTitle,
  OrderDetailsSection,
  OrderDetailsTitleContainer,
  OrderItemsPanel,
} from '../../components/OrderDetails/styles';

export function ExportPdfOrderDetails({ order }: { order: Order }) {
  const {
    orderId,
    eCommerceOrderName,
    firstName,
    lastName,
    companyName,
    telephone,
    items,
    createdByEmail: email,
    timestamp,
    totalPrice,
  } = order;

  const { country, city, address1, address2, postalCode } = order.address;
  const customerAccountFieldValue = order['vtaas-ui:checkout:customerAccountField'];
  const customerAccountFieldLabel = `${useTranslation('vtaas-ui:checkout:customerAccountField', 'Customer account')}:`;

  const customerReferenceFieldValue = order['vtaas-ui:checkout:customerReferenceField'];
  const customerReferenceFieldLabel = `${useTranslation('vtaas-ui:checkout:customerReferenceField', 'Customer ref')}:`;

  const messageToSellerFieldValue = order['vtaas-ui:checkout:messageToSellerField'];
  const messageToSellerFieldLabel = `${useTranslation(
    'vtaas-ui:checkout:messageToSellerField',
    'Message from customer'
  )}:`;

  return (
    <OrderDetailsPrintContainer>
      <OrderDetailsHeader>
        <OrderDetailsTitleContainer>
          <MUITypography variant="h5">Order details for {eCommerceOrderName || orderId}</MUITypography>
          <MUITypography variant="body1">Order received: {formatDateWithTime(timestamp)} </MUITypography>
        </OrderDetailsTitleContainer>
      </OrderDetailsHeader>
      <PrintOrderDetailsMainContent>
        <PrintOrderItemsPanel>
          <OrderDetailsSection sx={{ height: '100%' }}>
            <OrderDetailsPanelTitle>Parts ordered</OrderDetailsPanelTitle>
            {items.length !== 0 ? (
              <OrderPartsList items={items} totalPrice={totalPrice} />
            ) : /* istanbul ignore next */ null}
          </OrderDetailsSection>
        </PrintOrderItemsPanel>
        <PrintOrderDetailsPanel>
          <PrintDetailsAndAddress>
            <PrintOrderDetailsSection>
              <OrderDetailsPanelTitle>Customer details</OrderDetailsPanelTitle>
              <OrderDetailsData>
                <OrderDetailsDataRow>
                  <PrintOrderDetailsDataHeader>First name:</PrintOrderDetailsDataHeader>
                  <PrintOrderDetailsDataCell>{firstName}</PrintOrderDetailsDataCell>
                </OrderDetailsDataRow>
                <OrderDetailsDataRow>
                  <PrintOrderDetailsDataHeader>Last name:</PrintOrderDetailsDataHeader>
                  <PrintOrderDetailsDataCell>{lastName}</PrintOrderDetailsDataCell>
                </OrderDetailsDataRow>
                <OrderDetailsDataRow>
                  <PrintOrderDetailsDataHeader>Company name:</PrintOrderDetailsDataHeader>
                  <PrintOrderDetailsDataCell>{companyName}</PrintOrderDetailsDataCell>
                </OrderDetailsDataRow>
                <OrderDetailsDataRow>
                  <PrintOrderDetailsDataHeader>Telephone:</PrintOrderDetailsDataHeader>
                  <PrintOrderDetailsDataCell>{telephone}</PrintOrderDetailsDataCell>
                </OrderDetailsDataRow>
                <OrderDetailsDataRow>
                  <PrintOrderDetailsDataHeader>Email:</PrintOrderDetailsDataHeader>
                  <PrintOrderDetailsDataCell>{email}</PrintOrderDetailsDataCell>
                </OrderDetailsDataRow>
              </OrderDetailsData>
            </PrintOrderDetailsSection>
            <MUIDivider orientation="vertical" />
            <PrintOrderDetailsSection>
              <OrderDetailsPanelTitle>Delivery address</OrderDetailsPanelTitle>
              <OrderDetailsData>
                <OrderDetailsDataRow>
                  <PrintOrderDetailsDataHeader>Building name / number:</PrintOrderDetailsDataHeader>
                  <PrintOrderDetailsDataCell>{address1}</PrintOrderDetailsDataCell>
                </OrderDetailsDataRow>
                <OrderDetailsDataRow>
                  <PrintOrderDetailsDataHeader>Street address:</PrintOrderDetailsDataHeader>
                  <PrintOrderDetailsDataCell>{address2}</PrintOrderDetailsDataCell>
                </OrderDetailsDataRow>
                <OrderDetailsDataRow>
                  <PrintOrderDetailsDataHeader>Town / City:</PrintOrderDetailsDataHeader>
                  <PrintOrderDetailsDataCell>{city}</PrintOrderDetailsDataCell>
                </OrderDetailsDataRow>
                <OrderDetailsDataRow>
                  <PrintOrderDetailsDataHeader>Postcode:</PrintOrderDetailsDataHeader>
                  <PrintOrderDetailsDataCell>{postalCode}</PrintOrderDetailsDataCell>
                </OrderDetailsDataRow>
                <OrderDetailsDataRow>
                  <PrintOrderDetailsDataHeader>Country:</PrintOrderDetailsDataHeader>
                  <PrintOrderDetailsDataCell>{country}</PrintOrderDetailsDataCell>
                </OrderDetailsDataRow>
              </OrderDetailsData>
            </PrintOrderDetailsSection>
          </PrintDetailsAndAddress>
          <MUIDivider />
          <OrderDetailsSection>
            <PrintOrderDetailsData>
              <OrderDetailsDataRow>
                <PrintOrderDetailsDataHeader>{customerReferenceFieldLabel}</PrintOrderDetailsDataHeader>
                <PrintOrderDetailsDataCell>{customerReferenceFieldValue}</PrintOrderDetailsDataCell>
              </OrderDetailsDataRow>
              <OrderDetailsDataRow>
                <PrintOrderDetailsDataHeader>{customerAccountFieldLabel}</PrintOrderDetailsDataHeader>
                <PrintOrderDetailsDataCell>{customerAccountFieldValue}</PrintOrderDetailsDataCell>
              </OrderDetailsDataRow>
              <OrderDetailsDataRow>
                <PrintOrderDetailsDataHeader>{messageToSellerFieldLabel}</PrintOrderDetailsDataHeader>
                <PrintOrderDetailsDataCell sx={{ fontWeight: 'normal' }}>
                  {messageToSellerFieldValue}
                </PrintOrderDetailsDataCell>
              </OrderDetailsDataRow>
            </PrintOrderDetailsData>
          </OrderDetailsSection>
        </PrintOrderDetailsPanel>
      </PrintOrderDetailsMainContent>
    </OrderDetailsPrintContainer>
  );
}

const OrderDetailsPrintContainer = styled(MUIBox)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  margin: theme.spacing(4),

  p: {
    textAlign: 'left',
  },
}));

const PrintDetailsAndAddress = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  marginBottom: '2rem',
  gap: theme.spacing(2),
}));

const PrintOrderDetailsSection = styled(MUIBox)`
  display: flex;
  flex-direction: column;
  width: 50%;
`;

const PrintOrderDetailsMainContent = styled(OrderDetailsMainContent)(() => ({
  flexDirection: 'column',
}));

const PrintOrderDetailsPanel = styled(OrderDetailsPanel)(() => ({
  flexBasis: 'unset',
  minWidth: 'unset',
  overflow: 'unset',
}));

const PrintOrderItemsPanel = styled(OrderItemsPanel)(() => ({}));

const PrintOrderDetailsData = styled(OrderDetailsData)(({ theme }) => ({
  margin: theme.spacing(4),
}));

const PrintOrderDetailsDataHeader = styled(OrderDetailsDataHeader)`
  width: 30%;
`;

const PrintOrderDetailsDataCell = styled(OrderDetailsDataCell)`
  width: 70%;
`;
